import { Permission } from 'api/shared';
import groupBy from 'lodash-es/groupBy';
import { AccessBlock } from './access';
import { AtomicOperation, Operation } from './operations';
import { Resource } from './resources';

export const atomizePermission = (permission: Permission): Permission[] => {
  const allResources = Object.values(Resource).filter((resource) =>
    resource.startsWith(permission.scope),
  );
  const allOperations = Object.values(AtomicOperation).filter((operation) =>
    operation.startsWith(permission.operation),
  );

  return allResources.flatMap((resource) =>
    allOperations.map((operation) => ({
      scope: resource,
      operation,
    })),
  );
};

const allRead = [Operation.LIST, Operation.GET];
const allWrite = [Operation.CREATE, Operation.UPDATE, Operation.DELETE];

export function mergePermissions(userPermissions: Permission[]) {
  const groupedByScope = groupBy(userPermissions, (x) => x.scope);

  // array with merged read/write permissions
  const parsedUserPermissions: Permission[] = [];

  Object.entries(groupedByScope).forEach(([scope, permissions]) => {
    const hasAllRead = allRead.every((operation) =>
      permissions.some((p) => operation.startsWith(p.operation)),
    );
    const hasAllWrite = allWrite.every((operation) =>
      permissions.some((p) => operation.startsWith(p.operation)),
    );

    // If has all read and write permissions, merge them into ALL
    if (hasAllRead && hasAllWrite) {
      parsedUserPermissions.push({ scope, operation: Operation.ALL });
      return parsedUserPermissions;
    }

    // If can read all, add READ permission
    if (hasAllRead) {
      parsedUserPermissions.push({ scope, operation: Operation.READ });
    } else {
      // If cannot read all, add each permission separately
      permissions.forEach(
        (p) => p.operation.startsWith(Operation.READ) && parsedUserPermissions.push(p),
      );
    }

    // If can write all, add WRITE permission
    if (hasAllWrite) {
      parsedUserPermissions.push({ scope, operation: Operation.WRITE });
    } else {
      // If cannot write all, add each permission separately
      permissions.forEach(
        (p) => p.operation.startsWith(Operation.WRITE) && parsedUserPermissions.push(p),
      );
    }
  });

  return parsedUserPermissions;
}

// userPermissions needs to be merged
export const checkAllowed = (userPermissions: Permission[], permissions: AccessBlock[]) => {
  let allowed = false;

  userPermissions.forEach(({ scope, operation }) =>
    permissions.forEach((permission) => {
      const accessAllowed = permission.scope.startsWith(scope);
      const operationAllowed = permission.operation.startsWith(operation);

      if (accessAllowed && operationAllowed) allowed = true;
    }),
  );

  return allowed;
};
